/**
 * deploymentWatcher.js
 *
 * Polls /version.json every POLL_INTERVAL_MS milliseconds.
 * If the deployedAt timestamp differs from the baked-in timestamp
 * (process.env.VUE_APP_DEPLOY_TIMESTAMP), it triggers an Element UI
 * notification and hard-reloads the page.
 */

import { Notification } from 'element-ui'

const POLL_INTERVAL_MS = 2 * 60 * 1000
const AUTO_RELOAD_DELAY_MS = 10000

/**
 * Fetches /version.json with cache-busting.
 * Returns null silently on any network or parse error.
 */
async function fetchRemoteVersion() {
  try {
    const response = await fetch(`/version.json?_t=${Date.now()}`, {
      cache: 'no-store'
    })
    if (!response.ok) return null
    return await response.json()
  } catch {
    return null
  }
}

/**
 * Initializes the background deployment watcher.
 * Returns a cleanup/unsubscribe function to prevent leaks.
 */
export function initDeploymentWatcher() {
  const buildVersion = process.env.VUE_APP_DEPLOY_TIMESTAMP

  // Skip entirely in dev server mode or if env var is empty
  if (!buildVersion || process.env.NODE_ENV === 'development') {
    return () => {}
  }

  let pollingTimer = null
  let reloadTimer = null
  let visibilityHandler = null
  let updateTriggered = false
  const baselineVersion = buildVersion

  function triggerUpdate() {
    if (updateTriggered) return
    updateTriggered = true

    if (pollingTimer !== null) {
      clearInterval(pollingTimer)
      pollingTimer = null
    }
    if (visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }

    console.info(
      `[DeploymentWatcher] New deployment detected. Auto-reload in ${
        AUTO_RELOAD_DELAY_MS / 1000
      }s.`
    )

    // Auto-reload after the delay
    reloadTimer = setTimeout(() => {
      window.location.reload()
    }, AUTO_RELOAD_DELAY_MS)

    // Show Element UI Notification
    Notification({
      title: 'New Version Available',
      message: `A new version of the application has been deployed. The page will automatically reload in ${
        AUTO_RELOAD_DELAY_MS / 1000
      }s. Dismiss to reload now.`,
      type: 'warning',
      duration: 0,
      showClose: true,
      position: 'top-right',
      onClose: () => {
        if (reloadTimer !== null) {
          clearTimeout(reloadTimer)
          reloadTimer = null
        }
        window.location.reload()
      }
    })
  }

  const checkForUpdate = async() => {
    const remote = await fetchRemoteVersion()
    if (!remote) return

    if (remote.deployedAt !== baselineVersion) {
      triggerUpdate()
    }
  }

  // Check immediately on load
  checkForUpdate()

  // Start polling
  pollingTimer = setInterval(checkForUpdate, POLL_INTERVAL_MS)

  // Tab visibility change re-check
  visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate()
    }
  }
  document.addEventListener('visibilitychange', visibilityHandler)

  // Return cleanup closure
  return () => {
    if (pollingTimer !== null) {
      clearInterval(pollingTimer)
      pollingTimer = null
    }
    if (reloadTimer !== null) {
      clearTimeout(reloadTimer)
      reloadTimer = null
    }
    if (visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
  }
}
